import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import SimpleLoader from 'components/loaders/SimpleLoader';
import acApi from 'axios/ac/acApi';
import { customRangeToApi } from 'views/Admin/_shared/dateApi';
import { extractErrorMessage } from 'views/Admin/_shared/errorMessage';
import UsageReportFilters from './UsageReportFilters';
import UsageReportTable from './UsageReportTable';
import UsageReportExportButtons from './UsageReportExportButtons';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT,
  DEFAULT_USAGE_FILTERS,
  PageSize,
  UsageFilters,
  UsageReportRequest,
  UsageReportRow,
  UsageReportRowApi,
  UsageReportPage,
  UsageReportSort,
  UsageWorkspace,
} from './types';
import './UsageReport.scss';

const MIN_SEARCH_LENGTH = 2;

const buildRequest = (filters: UsageFilters, page: number, size: number, sort: UsageReportSort): UsageReportRequest => {
  const trimmedSearch = filters.searchTerm.trim();
  const periodPart =
    filters.period.kind === 'preset'
      ? { period: filters.period.value }
      : customRangeToApi(filters.period.from, filters.period.to);
  return {
    ...periodPart,
    workspaceIds: filters.workspaceIds.length > 0 ? filters.workspaceIds : undefined,
    search: trimmedSearch.length >= MIN_SEARCH_LENGTH ? trimmedSearch : undefined,
    includeDeletedMocks: filters.includeDeletedMocks,
    page,
    size,
    sort: [sort],
  };
};

const apiRowToRow = (row: UsageReportRowApi, workspaceNamesById: Map<number, string>): UsageReportRow => ({
  id: String(row.serviceMockId),
  serviceMockId: row.serviceMockId,
  serviceName: row.serviceName,
  workspaceId: row.workspaceId,
  workspaceName: workspaceNamesById.get(row.workspaceId) ?? '—',
  locationName: row.locationName,
  createdBy: row.createdBy,
  deployedAt: row.deployedAt,
  lastUsedAt: row.lastUsed,
  hits: row.hits,
  misses: row.misses,
  total: row.total,
  status: row.status,
});

const UsageReport: React.FC = () => {
  const { t } = useTranslation('admin');
  const dispatch = useDispatch();

  const workspaces = useSelector(
    (state: ApplicationState) => state.blazeUserConfig.blazeUserConfig?.user?.workspaces ?? [],
  );
  const usageWorkspaces: UsageWorkspace[] = useMemo(() => workspaces.map(w => ({ id: w.id, name: w.name })), [
    workspaces,
  ]);
  const workspaceNamesById = useMemo(() => new Map(workspaces.map(w => [w.id, w.name] as const)), [workspaces]);

  const [filters, setFilters] = useState<UsageFilters>(DEFAULT_USAGE_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<UsageReportSort>(DEFAULT_SORT);
  const [data, setData] = useState<UsageReportPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    const request = buildRequest(filters, page, pageSize, sort);
    acApi.adminReporting
      .getUsageReport(request)
      .then(response => {
        if (cancelled) return;
        const result = response.data.result as UsageReportPage;
        setData(result);
        setIsLoading(false);
      })
      .catch(error => {
        if (cancelled) return;
        setIsLoading(false);
        dispatch(
          showToastNotification(ToastType.Danger, t('usageReport.errors.loadFailed'), extractErrorMessage(error)),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [filters, page, pageSize, sort, dispatch, t]);

  const handleFiltersChange = useCallback((next: UsageFilters) => {
    setFilters(next);
    setPage(0);
  }, []);

  const handlePageChange = useCallback((next: number) => {
    setPage(next);
  }, []);

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize(next as PageSize);
    setPage(0);
  }, []);

  const handleSortChange = useCallback((next: UsageReportSort) => {
    setSort(next);
    // intentionally do not reset page — typical table UX
  }, []);

  const handleExportError = useCallback(
    (error: unknown) => {
      dispatch(
        showToastNotification(ToastType.Danger, t('usageReport.errors.exportFailed'), extractErrorMessage(error)),
      );
    },
    [dispatch, t],
  );

  const fetchAllRows = useCallback(async (): Promise<UsageReportRow[]> => {
    const total = data?.filteredRows ?? 0;
    if (total === 0) return [];
    const request = buildRequest(filters, 0, total, sort);
    const response = await acApi.adminReporting.getUsageReport(request);
    const result = response.data.result as UsageReportPage;
    return result.rows.map(r => apiRowToRow(r, workspaceNamesById));
  }, [data, filters, sort, workspaceNamesById]);

  const rows = useMemo(() => (data ? data.rows.map(r => apiRowToRow(r, workspaceNamesById)) : []), [
    data,
    workspaceNamesById,
  ]);

  return (
    <section className="admin-page usage-report-page">
      <h1>{t('pages.usageReport.title')}</h1>

      <div className="usage-report-toolbar">
        <UsageReportFilters filters={filters} workspaces={usageWorkspaces} onChange={handleFiltersChange} />
        <UsageReportExportButtons
          rows={rows}
          filters={filters}
          workspaceNamesById={workspaceNamesById}
          onError={handleExportError}
          fetchAllRows={fetchAllRows}
        />
      </div>

      <hr className="usage-report-divider" />

      {isLoading && rows.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SimpleLoader />
        </div>
      ) : (
        <UsageReportTable
          rows={rows}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          totalRows={data?.filteredRows ?? 0}
          sort={sort}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSortChange}
        />
      )}
    </section>
  );
};

export default UsageReport;
